<template>
    <div class="kiosk-mode-configuration">
        <ScreenSaver
            v-if="kiosk.isScreenSaverLoaded"
            :images="kiosk.screenSaverImages"
            :isVisible="kiosk.isScreenSaverVisible"
            :interval="kiosk.screenSaverInterval"
            @click="onScreenSaverClick"
        />
        <div v-if="kiosk.isResettingSession" class="reset-session-indicator">
            <span>{{ translations.general.loading }}</span>
        </div>
        <template v-if="kiosk.isKioskDebugMode">
            <button type="button" class="btn btn-primary mr-1" @click="kiosk.toggleKioskFullscreen">
                <span>Kiosk Fullscreen</span>
            </button>
            <button type="button" class="btn btn-primary mr-1" @click="onShowScreenSaverToggle">
                <span>Show screensaver</span>
            </button>
            <button type="button" class="btn btn-primary mr-1" @click="() => kiosk.resetSession()">Reset</button>
            <div>
                <span>Identity ID: {{ identity.identityId }}</span>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { useKioskStore } from "@/app/stores/kiosk";
    import { useIdentityStore } from "@/data/stores/identity";
    import { translations } from "@/shared/localization/translations";

    import ScreenSaver from "./ScreenSaver.vue";

    const identity = useIdentityStore();
    const kiosk = useKioskStore();

    function onShowScreenSaverToggle() {
        if (kiosk.isScreenSaverVisible) {
            kiosk.hideScreenSaver();
        } else {
            kiosk.showScreenSaver();
        }
    }

    async function onScreenSaverClick() {
        kiosk.hideScreenSaver();
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // the two terms of the bottom band, named so the bar below reads as the sum. the
    // caption doubles with the band, the button only grows by half again
    $kioskSelectionSumHeight: 38px * variables.$kioskBottomScale;
    $kioskPayBtnHeight: 56px * variables.$kioskScale * variables.$kioskBottomBtnScale;

    // selection sum + pay button + the gap under it, which is a share of the selection sum
    // band above the button instead of the app's unscaled 10px
    $kioskCartBottomGap: $kioskSelectionSumHeight * variables.$kioskBottomBtnGapFactor;
    $kioskCartBottomHeight: $kioskSelectionSumHeight + $kioskPayBtnHeight + $kioskCartBottomGap;

    // the bill bar keeps the unscaled caption (9 + 20 + 9), so its gap is a share of that
    $kioskBillBottomGap: (9px + 20px + 9px) * variables.$kioskBottomBtnGapFactor;

    .kiosk-mode-configuration {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1000;
    }

    .kiosk-mode .app {
        max-width: 100% !important;

        .view > .head > .usermenu {
            display: none !important;
        }
    }

    // header scaled from the app's 52px + 1px border, so 78px + 1px border. the 79px the
    // views use as their top offset is this height plus that unscaled border
    .kiosk-mode .app > .view > .head {
        height: 52px * variables.$kioskScale;

        > .title {
            line-height: 52px * variables.$kioskScale;
            font-size: 16px * variables.$kioskScale;
            letter-spacing: 0.5px * variables.$kioskScale;
        }

        > .action {
            width: 68px * variables.$kioskScale;
            height: 52px * variables.$kioskScale;
            line-height: 52px * variables.$kioskScale;
            font-size: 18px * variables.$kioskScale;

            > .fal {
                font-size: 18px * variables.$kioskScale;
            }
        }

        // sits on the right side of the head, where the usermenu would be if kiosk mode
        // did not hide it. the inset is a placement, not a scaled value
        > .language-selector {
            height: 52px * variables.$kioskScale;
            left: auto;
            right: 44px;

            .language-button {
                width: 44px * variables.$kioskScale;
                height: 44px * variables.$kioskScale;
                border-radius: 12px * variables.$kioskScale;

                > i {
                    font-size: 22px * variables.$kioskScale;
                }
            }
        }
    }

    // the big selector in the bottom right corner, bigger than the one in the head. the services
    // view keeps it outside of its head so it can be placed here
    .screen-saver > .language-selector,
    .kiosk-mode .app > .services > .language-selector {
        top: auto;
        left: auto;
        right: 40px;
        bottom: 40px;
        height: auto;

        .language-button {
            width: 96px;
            height: 96px;
            border-radius: 24px;

            > i {
                font-size: 48px;
            }
        }
    }

    // on the screen saver it has to stay above the rotating images, which carry an inline
    // z-index of 10000
    .screen-saver > .language-selector {
        z-index: 10001;
    }

    // the views place their content below the head, keep the offsets in sync
    .kiosk-mode .app {
        > .services > .scrollable,
        > .cart > .itemlist,
        > .receipt > .scrollable,
        > .payment-receipt > .scrollable {
            top: 79px;
        }

        // follows the larger parallax logo
        > .services > .scrollable.hasparallax {
            padding-top: 114px * variables.$kioskScale;
        }

        // room for the selection sum and the bigger pay button
        > .order > .bottom,
        > .cart > .bottom {
            height: $kioskCartBottomHeight;
        }

        > .order > .bottom > .selectionsum,
        > .cart > .bottom > .selectionsum {
            height: $kioskSelectionSumHeight;
            line-height: $kioskSelectionSumHeight;
            font-size: 14px * variables.$kioskBottomScale;
            letter-spacing: 0.3px * variables.$kioskScale * variables.$kioskBottomScale;
        }

        > .cart > .itemlist {
            bottom: $kioskCartBottomHeight;
            padding: 0 variables.$listVerticalPadding * variables.$kioskScale * variables.$kioskVerticalPaddingGapFactor;
        }

        // the bar grows with the button, keep the summary clear of it. the bill bar
        // carries the unscaled .selected, not .selectionsum, so it stays shorter
        > .bill .ordersummary {
            bottom: 9px + 20px + 9px + $kioskPayBtnHeight + $kioskBillBottomGap;
        }

        // the bar has no height of its own, the gap under the button is its bottom padding
        > .bill > .bottom {
            padding-bottom: $kioskBillBottomGap;
        }

        // continue button follows the bigger kiosk paybtn
        > .done > .bottom > .btn {
            height: $kioskPayBtnHeight;
            border-radius: 28px * variables.$kioskScale * variables.$kioskBottomBtnScale;
            font-size: 22px * variables.$kioskScale * variables.$kioskBottomBtnScale;
        }

        // the bar is its caption (20 + 11), its 10px bottom padding and the 8px clearance
        // the base 95px carries over its 87px of content - all doubled with the band -
        // plus the button, which grows by half again instead
        > .done > .scrollable {
            bottom: (20px + 11px + 10px + 8px) * variables.$kioskScale * variables.$kioskBottomScale +
                $kioskPayBtnHeight;
        }

        // floats above the order bar: the bar height plus the base 13px clearance
        > .fulfillmentbtn {
            bottom: $kioskCartBottomHeight + 13px * variables.$kioskScale;
        }

        // the bar is opaque, so the overview stops above it instead of running underneath
        // it. that keeps both columns' scrollbars inside the visible area, and it replaces
        // the bottom padding that used to be needed to make the last item row reachable
        .kiosk-offer-overview {
            bottom: $kioskCartBottomHeight;
        }
    }

    .kiosk-mode .view > .dim {
        top: 79px;
    }

    .kiosk-mode-normal-width {
        .app {
            max-width: 640px !important;
        }
    }

    .kiosk-mode-resetting-session {
        .view.allocation * {
            display: none !important;
        }
    }

    .reset-session-indicator {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: white;
        z-index: 1000;
        display: flex;
        justify-content: center;
        align-items: center;

        font-size: 40px;
    }
</style>
